import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { UserDetailsForm } from './UserDetailsForm';
import { EmailTemplate } from './EmailTemplate';
import { Notification } from './Notification';
import type { CartItem } from '../types';

export interface CheckoutFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface CheckoutFormProps {
  items: CartItem[];
  total: number;
  deposit: number;
  onClose: () => void;
  onSubmit: (data: CheckoutFormData) => void;
}

export function CheckoutForm({ items, total, deposit, onClose, onSubmit }: CheckoutFormProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isQuote, setIsQuote] = useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  const prepareWebhookData = (formData: FormData, type: 'payment' | 'quote') => {
    const customerDetails = {
      email: formData.get('email') as string,
      firstName: formData.get('firstName') as string,
      lastName: formData.get('lastName') as string,
      phone: formData.get('phone') as string,
    };

    const lineItems = items.map(item => ({
      id: item.id,
      name: item.name,
      basePrice: item.price,
      optionsCost: item.optionsCost || 0,
      totalPrice: item.price + (item.optionsCost || 0),
      quantity: item.quantity,
      options: Object.entries(item.selectedOptions).map(([key, value]) => ({
        name: key,
        value: value,
        additionalCost: item.options
          .find(opt => opt.name === key)
          ?.values.find(v => v.value === value)
          ?.additionalCost || 0
      })),
      total: (item.price + (item.optionsCost || 0)) * item.quantity
    }));

    const emailHtml = EmailTemplate({
      customerDetails,
      items,
      total,
      deposit,
      type: type === 'payment' ? 'order' : 'quote'
    });

    return {
      type,
      customer: customerDetails,
      order: {
        items: lineItems,
        subtotal: total,
        deposit: deposit,
        currency: 'gbp'
      },
      emailTemplate: emailHtml,
      ...(type === 'payment' ? {
        payment: {
          amount: deposit,
          currency: 'gbp'
        }
      } : {})
    };
  };

  const sendWebhookRequest = async (data: any, type: 'payment' | 'quote') => {
    const endpoint = type === 'payment' 
      ? 'https://n8n-34gc.onrender.com/webhook/b7eeb586-42ee-471d-b1ad-5ecce76fa178'
      : 'https://n8n-34gc.onrender.com/webhook/b120b763-8e58-4ae6-9fec-530a0a1b1b53';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (type === 'payment') {
        return await response.json();
      }

      return { success: response.status === 200 };
    } catch (error) {
      console.error('Webhook request failed:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      const formData = new FormData(e.target as HTMLFormElement);
      const data = prepareWebhookData(formData, 'payment');
      
      const response = await sendWebhookRequest(data, 'payment');
      
      if (response.url) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = response.url;
        }, 1500);
      } else {
        throw new Error('No payment URL received');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment processing failed. Please try again.');
    } finally {
      if (!success) {
        setIsProcessing(false);
      }
    }
  };

  const handleQuoteRequest = async () => {
    if (!formRef.current) return;
    
    setIsProcessing(true);
    setError(null);
    setIsQuote(true);

    try {
      const formData = new FormData(formRef.current);
      const data = prepareWebhookData(formData, 'quote');
      const response = await sendWebhookRequest(data, 'quote');
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error('Failed to send quote');
      }
    } catch (err) {
      console.error('Quote error:', err);
      setError('Failed to send quote request. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
          <Notification
            type="success"
            message={isQuote 
              ? "Quote request sent successfully! We'll be in touch soon."
              : "Payment link generated! Redirecting to secure payment page..."}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <img src="https://img.adminteam.work/lecmg.png" alt="LEC Digital Design" className="h-8 w-auto" />
              <h2 className="text-2xl font-semibold">Checkout</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
            <UserDetailsForm />

            {error && (
              <Notification
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            )}

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-[#6389af] text-white py-3 rounded-md hover:bg-[#557694] transition-colors disabled:bg-[#6389af]/50 flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 size={20} className="animate-spin" />}
                {isProcessing ? 'Processing Payment...' : 'Complete Purchase'}
              </button>

              <button
                type="button"
                onClick={handleQuoteRequest}
                disabled={isProcessing}
                className="w-full bg-white text-[#6389af] border-2 border-[#6389af] py-3 rounded-md hover:bg-[#6389af]/10 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isProcessing && <Loader2 size={20} className="animate-spin" />}
                {isProcessing ? 'Processing...' : 'Send as Quote'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
